import { GroupProps } from '@react-three/fiber'
import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { Ruler3D } from 'features/assetViz/objects/decor/Ruler3D'
import { Surface3D } from 'features/assetViz/objects/surfaces/Surface3D'
import { planeGeometry } from 'features/assetViz/utils/geometryDefines'
import { extractScale, Vec3 } from 'features/assetViz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import {
  GeckoComponentRect,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

/**
 * A simple, flat, 3D plane in X and Y coordinates. The dimensions of this
 * plane will be derived purely from its component's size
 * @constructor
 */
export const BasicPlaneSurface3D: React.FC<GroupProps> = (groupProps) => {
  //----------------------------------------------------------------------------

  const { focusedCDF } = useAssetVizInternal()

  const { bounds, scale } = React.useMemo(() => {
    const bounds =
      focusedCDF?.data.geometry ??
      GeckoComponentRect.CreateFromXYs(
        '',
        0,
        0,
        1,
        1,
        GeckoMeasurementUnit.Feet,
        GeckoMeasurementUnit.Feet
      )
    const scale = extractScale(groupProps.scale).multiply(
      new Vector3(bounds.x.end.num, bounds.y.end.num, 1.0)
    )
    return { bounds, scale }
  }, [focusedCDF, groupProps.scale])

  //----------------------------------------------------------------------------

  const positionXTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const convertedCoord = coord
        .convertTo(bounds.x.unit)
        .minus(bounds.start.x)
        .scale(1.0 / bounds.width.num)
      return [convertedCoord.num, 0, 0]
    },
    [bounds]
  )
  const positionYTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const convertedCoord = coord
        .convertTo(bounds.y.unit)
        .minus(bounds.start.y)
        .scale(1.0 / bounds.height.num)
      return [0, convertedCoord.num, 0]
    },
    [bounds]
  )

  //----------------------------------------------------------------------------

  return (
    <>
      <Surface3D geometry={planeGeometry} bounds={bounds} scale={scale}>
        {/* Left ruler ------------------------------------------------------- */}
        <Ruler3D
          anchor='right'
          start={bounds.start.y}
          end={bounds.end.y}
          includeTicks={true}
          includeStart={true}
          includeEnd={true}
          getTickPosition={positionYTick}
        />
        {/* Bottom ruler ----------------------------------------------------- */}
        <Ruler3D
          anchor='top'
          start={bounds.start.x}
          end={bounds.end.x}
          includeTicks={true}
          includeStart={true}
          includeEnd={true}
          getTickPosition={positionXTick}
        />
      </Surface3D>
    </>
  )
}
